/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Sales Order API Service
 * CRUD + status transition endpoints for /api/v1/venta/sales-orders
 */

import { get, post, put } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type { PagedResult } from '../types/product';
import type {
  SalesOrderListItem,
  SalesOrderDetail,
  SalesOrderStats,
  CreateSalesOrderRequest,
  UpdateSalesOrderRequest,
} from '../types/sales-order';

const BASE = '/venta/sales-orders';

export interface SalesOrderListParams {
  search?: string;
  status?: string;
  partnerId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
  sort?: string;
  direction?: string;
}

export async function getSalesOrders(params: SalesOrderListParams = {}): Promise<PagedResult<SalesOrderListItem>> {
  const queryParams: Record<string, unknown> = {};
  if (params.search) queryParams.search = params.search;
  if (params.status) queryParams.status = params.status;
  if (params.partnerId) queryParams.partnerId = params.partnerId;
  if (params.dateFrom) queryParams.dateFrom = params.dateFrom;
  if (params.dateTo) queryParams.dateTo = params.dateTo;
  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.direction) queryParams.direction = params.direction;

  const response = await get<ApiResponse<PagedResult<SalesOrderListItem>>>(BASE, queryParams);
  return response.data;
}

export async function getSalesOrder(id: string): Promise<SalesOrderDetail> {
  const response = await get<ApiResponse<SalesOrderDetail>>(`${BASE}/${id}`);
  return response.data;
}

export async function getSalesOrderStats(): Promise<SalesOrderStats> {
  const response = await get<ApiResponse<SalesOrderStats>>(`${BASE}/stats`);
  return response.data;
}

export async function createSalesOrder(data: CreateSalesOrderRequest): Promise<SalesOrderDetail> {
  const response = await post<ApiResponse<SalesOrderDetail>>(BASE, data);
  return response.data;
}

export async function createFromQuotation(quotationId: string): Promise<SalesOrderDetail> {
  const response = await post<ApiResponse<SalesOrderDetail>>(`${BASE}/from-quotation/${quotationId}`);
  return response.data;
}

export async function updateSalesOrder(id: string, data: UpdateSalesOrderRequest): Promise<SalesOrderDetail> {
  const response = await put<ApiResponse<SalesOrderDetail>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function confirmOrder(id: string): Promise<SalesOrderDetail> {
  const response = await post<ApiResponse<SalesOrderDetail>>(`${BASE}/${id}/confirm`);
  return response.data;
}

export async function fulfillOrder(id: string): Promise<SalesOrderDetail> {
  const response = await post<ApiResponse<SalesOrderDetail>>(`${BASE}/${id}/fulfill`);
  return response.data;
}

export async function cancelOrder(id: string): Promise<SalesOrderDetail> {
  const response = await post<ApiResponse<SalesOrderDetail>>(`${BASE}/${id}/cancel`);
  return response.data;
}

export const salesOrderService = {
  getSalesOrders,
  getSalesOrder,
  getSalesOrderStats,
  createSalesOrder,
  createFromQuotation,
  updateSalesOrder,
  confirmOrder,
  fulfillOrder,
  cancelOrder,
};
